import React from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, Globe, ShoppingCart, TrendingDown, TrendingUp } from 'lucide-react';

const SYMBOLS = { USD: '$', EUR: '€', GBP: '£', AED: 'AED ' };

export const formatLakh = (value) => {
  const amount = Number(value || 0);
  if (amount >= 10000000) return `${(amount / 10000000).toFixed(2)}Cr`;
  if (amount >= 100000) return `${(amount / 100000).toFixed(2)}L`;
  if (amount >= 1000) return `${(amount / 1000).toFixed(1)}K`;
  return amount.toFixed(0);
};

const THEMES = {
  blue: { chip: 'bg-blue-50 text-blue-600', bar: 'bg-blue-500/70', card: 'from-blue-50/60', value: 'text-slate-900' },
  amber: { chip: 'bg-amber-50 text-amber-600', bar: 'bg-amber-400/80', card: 'from-amber-50/60', value: 'text-slate-900' },
  emerald: { chip: 'bg-emerald-50 text-emerald-600', bar: 'bg-emerald-500/70', card: 'from-emerald-50/60', value: 'text-emerald-600' },
  violet: { chip: 'bg-violet-50 text-violet-600', bar: 'bg-violet-500/70', card: 'from-violet-50/60', value: 'text-violet-600' },
};

const DeltaBadge = ({ value }) => {
  if (value === null || value === undefined) return null;
  const up = value >= 0;
  const Icon = up ? TrendingUp : TrendingDown;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${up ? 'bg-emerald-50 text-emerald-600' : 'bg-rose-50 text-rose-600'}`}>
      <Icon className="h-3 w-3" strokeWidth={2} />{Math.abs(value)}%
    </span>
  );
};

const Sparkbars = ({ series = [], theme }) => {
  const peak = Math.max(1, ...series);
  return (
    <div className="flex h-10 items-end gap-1" aria-hidden="true">
      {series.map((point, index) => (
        <span
          key={index}
          className={`w-2 rounded-sm ${THEMES[theme].bar}`}
          style={{ height: `${Math.max(12, (point / peak) * 100)}%`, opacity: 0.45 + (index / Math.max(1, series.length - 1)) * 0.55 }}
        />
      ))}
    </div>
  );
};

const KpiCard = ({ label, icon: Icon, theme, delta, series, footer, children, onClick, testId }) => (
  <button
    type="button"
    onClick={onClick}
    className={`group flex w-full flex-col gap-4 rounded-2xl border border-slate-200/80 bg-gradient-to-br ${THEMES[theme].card} via-white to-white p-5 text-left shadow-[0_1px_2px_rgba(16,24,40,0.04),0_8px_24px_-16px_rgba(16,24,40,0.18)] transition-shadow duration-200 hover:shadow-[0_1px_2px_rgba(16,24,40,0.06),0_16px_32px_-18px_rgba(16,24,40,0.28)] focus:outline-none focus:ring-2 focus:ring-blue-500/40`}
    data-testid={testId}
  >
    <div className="flex items-start justify-between gap-3">
      <span className={`flex h-11 w-11 items-center justify-center rounded-xl ${THEMES[theme].chip}`}>
        <Icon className="h-5 w-5" strokeWidth={1.75} />
      </span>
      <div className="flex-1">
        <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500">{label}</p>
      </div>
      <DeltaBadge value={delta} />
    </div>
    <div className="flex items-end justify-between gap-3">
      <div className="min-w-0">
        {children}
        <p className="mt-1 text-xs text-slate-500">{footer}</p>
      </div>
      <Sparkbars series={series} theme={theme} />
    </div>
  </button>
);

const CurrencyStack = ({ byCurrency, className }) => {
  const entries = Object.entries(byCurrency || {});
  if (!entries.length) return <p className="text-3xl font-light text-slate-300">—</p>;
  return (
    <div className="space-y-0.5">
      {entries.slice(0, 2).map(([currency, value]) => (
        <p key={currency} className={`font-mono text-xl font-bold leading-tight tabular-nums ${className}`}>
          <span className="mr-1 text-[11px] font-semibold text-slate-400">{currency}</span>
          {SYMBOLS[currency] || ''}{formatLakh(value)}
        </p>
      ))}
    </div>
  );
};

/** Four commercial KPIs — quotes out, orders in — with real period-over-period deltas. */
export const HeroCommercialBand = ({ metrics }) => {
  const navigate = useNavigate();
  const comparison = metrics?.hero_comparison || {};
  const deltas = comparison.deltas || {};
  const series = (key) => (comparison.series || []).map((point) => Number(point[key] || 0));

  return (
    <section data-testid="hero-commercial-band">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
        <KpiCard
          label="Domestic quotes" icon={FileText} theme="blue" delta={deltas.domestic_quotes}
          series={series('domestic_quotes')} footer={`${metrics?.domestic_quotes?.count || 0} quotations`}
          onClick={() => navigate('/quotations?currency=INR')} testId="hero-domestic-quotes"
        >
          <p className="font-mono text-3xl font-bold leading-none tabular-nums text-slate-900">
            <span className="mr-1 text-lg font-normal text-slate-400">₹</span>{formatLakh(metrics?.domestic_quotes?.value)}
          </p>
        </KpiCard>

        <KpiCard
          label="Export quotes" icon={Globe} theme="amber" delta={deltas.export_quotes}
          series={series('export_quotes')} footer={`${metrics?.export_quotes?.count || 0} quotations`}
          onClick={() => navigate('/quotations?type=export')} testId="hero-export-quotes"
        >
          <CurrencyStack byCurrency={metrics?.export_quotes?.by_currency} className="text-slate-900" />
        </KpiCard>

        <KpiCard
          label="Domestic order book" icon={ShoppingCart} theme="emerald" delta={deltas.domestic_orders}
          series={series('domestic_orders')} footer={`${metrics?.domestic_orders?.count || 0} sales orders`}
          onClick={() => navigate('/orders')} testId="hero-domestic-orders"
        >
          <p className="font-mono text-3xl font-bold leading-none tabular-nums text-emerald-600">
            <span className="mr-1 text-lg font-normal text-slate-400">₹</span>{formatLakh(metrics?.domestic_orders?.value)}
          </p>
        </KpiCard>

        <KpiCard
          label="Export order book" icon={Globe} theme="violet" delta={deltas.export_orders}
          series={series('export_orders')} footer={`${metrics?.export_orders?.count || 0} sales orders`}
          onClick={() => navigate('/orders')} testId="hero-export-orders"
        >
          <CurrencyStack byCurrency={metrics?.export_orders?.by_currency} className="text-violet-600" />
        </KpiCard>
      </div>
      <p className="mt-3 flex flex-wrap items-center gap-x-2 text-xs text-slate-500" data-testid="hero-open-orders">
        <span className="font-mono font-semibold tabular-nums text-slate-800">{metrics?.orders_open_count || 0}</span> orders open
        <span className="text-slate-300">|</span>
        <span className="font-mono font-semibold tabular-nums text-slate-800">₹{formatLakh(metrics?.orders_open_value_inr)}</span> yet to be delivered
      </p>
    </section>
  );
};
